import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import { Alert, Button, Chip, Stack, TextField } from '@mui/material';
import type { CellValueChangedEvent, ColDef } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { usePagination } from '@tiles-erp/hooks';
import { PageContainer, useSaveShortcut } from '@tiles-erp/ui';
import type { ProductItem } from '@tiles-erp/shared-types';
import { DataTable } from '../../components/DataTable';
import { ApiError } from '../../lib/api-client';
import { batchesOf } from '../../lib/batches';
import { ProductFilterBar } from './ProductFilterBar';
import { useBulkUpdateRates, useProducts } from './api';
import type { ProductFilters } from './api';

interface WeightEdit {
  weightKg: number;
  purchaseRate: number;
  transportRate: number;
  additionalRate: number;
  gstRate: number;
  version: number;
}
type WeightRow = ProductItem & { weightKg: number };

const validWeight = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

/** Bulk product-weight editor using the same filtering and batch-save workflow as rates. */
export function ProductWeightsPage(): JSX.Element {
  const pagination = usePagination({ initialPageSize: 50 });
  const [filters, setFilters] = useState<ProductFilters>({});
  const { data, isFetching, refetch } = useProducts(pagination.query, filters);
  const bulkUpdate = useBulkUpdateRates();
  const [edits, setEdits] = useState<Record<string, WeightEdit>>({});
  const [bulkValue, setBulkValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedCount, setSavedCount] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const products = data?.items ?? [];

  const rows = useMemo<WeightRow[]>(() => products.map((product) => ({
    ...product,
    weightKg: edits[product.id]?.weightKg ?? product.weightKg ?? 0,
  })), [products, edits]);

  const onCellValueChanged = (event: CellValueChangedEvent<WeightRow>): void => {
    const row = event.data;
    if (!row) return;
    setSavedCount(null);
    setEdits((previous) => ({
      ...previous,
      [row.id]: {
        weightKg: validWeight(row.weightKg, row.weightKg ?? 0),
        purchaseRate: row.purchaseRate ?? 0,
        transportRate: row.transportRate,
        additionalRate: row.additionalRate,
        gstRate: row.gstRate,
        version: previous[row.id]?.version ?? row.version,
      },
    }));
  };

  const applyToAll = (): void => {
    const weightKg = Number(bulkValue);
    if (!Number.isFinite(weightKg) || weightKg < 0) {
      setError('Enter a valid non-negative weight');
      return;
    }
    setError(null);
    setSavedCount(null);
    setEdits((previous) => {
      const next = { ...previous };
      for (const product of products) next[product.id] = {
        weightKg,
        purchaseRate: product.purchaseRate ?? 0,
        transportRate: product.transportRate,
        additionalRate: product.additionalRate,
        gstRate: product.gstRate,
        version: previous[product.id]?.version ?? product.version,
      };
      return next;
    });
  };

  const save = async (): Promise<void> => {
    setError(null);
    setSavedCount(null);
    setSaving(true);
    try {
      const items = Object.entries(edits).map(([productId, edit]) => ({
          productId,
          weightKg: edit.weightKg,
          purchaseRate: edit.purchaseRate,
          transportRate: edit.transportRate,
          additionalRate: edit.additionalRate,
          gstRate: edit.gstRate,
          version: edit.version,
      }));
      let count = 0;
      for (const batch of batchesOf(items)) count += (await bulkUpdate.mutateAsync({ items: batch })).length;
      setEdits({});
      setSavedCount(count);
    } catch (caught) {
      setError(caught instanceof ApiError || caught instanceof Error ? caught.message : 'Failed to save weights');
      void refetch();
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo<ColDef<WeightRow>[]>(() => [
    { field: 'sku', headerName: 'SKU', minWidth: 140 },
    { field: 'name', headerName: 'Product', minWidth: 240 },
    { field: 'brandName', headerName: 'Brand', minWidth: 140 },
    { field: 'categoryName', headerName: 'Category', minWidth: 170 },
    { field: 'sizeMm', headerName: 'Size', maxWidth: 110 },
    { field: 'piecesPerBox', headerName: 'Pcs/box', maxWidth: 110 },
    {
      field: 'weightKg', headerName: 'Weight per box (kg)', editable: true, maxWidth: 150,
      valueParser: (params) => validWeight(params.newValue, validWeight(params.oldValue, 0)),
      cellStyle: { backgroundColor: 'rgba(11, 95, 255, 0.06)', fontWeight: 600 },
    },
  ], []);

  useSaveShortcut(() => void save());
  const dirtyCount = Object.keys(edits).length;
  return (
    <PageContainer
      title="Product weights"
      subtitle="Filter products, edit the blue weight cells, or apply one weight to all visible products."
      actions={<Stack direction="row" spacing={1} alignItems="center">
        {dirtyCount > 0 && <Chip label={`${dirtyCount} unsaved`} color="warning" size="small" />}
        <Button variant="outlined" startIcon={<RestartAltIcon />} disabled={!dirtyCount} onClick={() => setEdits({})}>Discard</Button>
        <Button variant="contained" startIcon={<SaveIcon />} disabled={!dirtyCount || saving} onClick={() => void save()}>
          {saving ? 'Saving…' : `Save ${dirtyCount || ''}`}
        </Button>
      </Stack>}
    >
      <Stack spacing={1.5}>
        {error && <Alert severity="error">{error}</Alert>}
        {savedCount !== null && <Alert severity="success">Updated weight for {savedCount} products.</Alert>}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ProductFilterBar value={filters} showSeries onChange={(next) => { setFilters(next); pagination.setPage(1); }} />
          <TextField size="small" type="number" label="Weight per box (kg)" value={bulkValue} onChange={(event) => setBulkValue(event.target.value)} sx={{ width: 140 }} inputProps={{ min: 0, step: 0.001 }} />
          <Button variant="outlined" startIcon={<PlaylistAddCheckIcon />} onClick={applyToAll} disabled={bulkValue === '' || !products.length}>Apply ({products.length})</Button>
        </Stack>
        <DataTable rows={rows} columns={columns} meta={data?.meta} pagination={pagination} loading={isFetching} searchPlaceholder="Search by name, SKU…" height={620} gridOptions={{ getRowId: (params) => params.data.id, onCellValueChanged, stopEditingWhenCellsLoseFocus: true, singleClickEdit: true }} />
      </Stack>
    </PageContainer>
  );
}
